<template>
  <div>
    <b-card
      title="Ajouter un membre du personnel"
      aria-label="Formulaire d’ajout de membre du personnel"
    >
      <b-form
        ref="staffForm"
        aria-labelledby="staff-form-title"
        role="form"
        @submit.prevent="saveStaff"
      >
        <!-- Titre Visuellement caché pour l’accessibilité -->
        <h1
          id="staff-form-title"
          class="sr-only"
        >
          Formulaire d’ajout de membre du personnel
        </h1>
        <!-- Sélection du rôle (déplacé en haut) -->
        <h5
          id="section-role"
          class="mb-2"
        >
          <feather-icon
            icon="ShieldIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Sélection du rôle
        </h5>

        <b-form-group
          label="Rôle professionnel *"
          label-for="role"
          :invalid-feedback="submitted && !form.role ? 'Le rôle est requis' : ''"
          :state="roleState"
          aria-required="true"
          description="Sélectionnez le rôle logistique du membre du personnel."
        >
          <v-select
            id="role"
            v-model="form.role"
            :options="staffRoles"
            label="label"
            :reduce="role => role.value"
            placeholder="Sélectionnez un rôle professionnel"
            :class="[
              'enhanced-select',
              submitted && !form.role ? 'is-invalid' : '',
              form.role ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            aria-describedby="role-desc"
            aria-required="true"
            @input="onRoleChange"
          />
          <b-form-text
            id="role-desc"
            class="sr-only"
          >
            Champ obligatoire. Sélectionnez le rôle professionnel du membre du personnel.
          </b-form-text>
        </b-form-group>

        <!-- Informations personnelles -->
        <h5
          id="section-infos-personnelles"
          class="mb-2"
        >
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nom complet *"
              label-for="username"
              aria-required="true"
              description="Nom et prénom du membre du personnel, tel qu’indiqué sur les documents officiels."
            >
              <b-form-input
                id="username"
                v-model="form.username"
                :state="nameState"
                placeholder="Entrez le nom complet"
                aria-describedby="username-desc"
                aria-required="true"
                autocomplete="name"
              />
              <b-form-text
                id="username-desc"
                class="sr-only"
              >
                Champ obligatoire. Saisissez le nom et le prénom complets.
              </b-form-text>
              <b-form-invalid-feedback v-if="submitted && !form.username">
                Le nom complet est requis
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Adresse e-mail professionnelle *"
              label-for="email"
              aria-required="true"
              description="Adresse e-mail utilisée pour la connexion et la communication interne."
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                :state="emailState"
                placeholder="prenom.nom@entreprise.fr"
                aria-describedby="email-desc"
                aria-required="true"
                autocomplete="email"
              />
              <b-form-text
                id="email-desc"
                class="sr-only"
              >
                Champ obligatoire. Saisissez une adresse e-mail professionnelle valide.
              </b-form-text>
              <b-form-invalid-feedback v-if="submitted && !form.email">
                L'adresse e-mail est requise
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.email && !emailState">
                Veuillez entrer une adresse e-mail valide
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Téléphone professionnel"
              label-for="phone"
              description="Numéro de téléphone mobile professionnel (8 chiffres, sans espaces)."
            >
              <b-form-input
                id="phone"
                v-model="form.phone"
                placeholder="Numéro de téléphone professionnel"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                :state="phoneState"
                aria-describedby="phone-desc"
                autocomplete="tel"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text
                id="phone-desc"
                class="sr-only"
              >
                Format : 8 chiffres, sans espaces.
              </b-form-text>
              <b-form-invalid-feedback v-if="form.phone && !phoneState">
                Le numéro de téléphone doit contenir 8 chiffres
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Téléphone secondaire"
              label-for="phone2"
              description="Numéro de téléphone secondaire (optionnel)."
            >
              <b-form-input
                id="phone2"
                v-model="form.phone2"
                placeholder="Numéro de téléphone secondaire (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                aria-describedby="phone2-desc"
                autocomplete="tel"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text
                id="phone2-desc"
                class="sr-only"
              >
                Format : 8 chiffres, sans espaces.
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Adresse postale"
          label-for="address"
          description="Adresse complète du membre du personnel (rue, ville, code postal)."
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Exemple : 12 rue de la Logistique, 75000 Paris"
            rows="3"
            aria-describedby="address-desc"
            autocomplete="street-address"
          />
          <b-form-text
            id="address-desc"
            class="sr-only"
          >
            Saisissez l’adresse complète, y compris la ville et le code postal.
          </b-form-text>
        </b-form-group>

        <b-form-group
          label="Numéro de CIN (optionnel)"
          label-for="cin"
          description="Carte d’Identité Nationale (8 chiffres, sans espaces)."
        >
          <b-form-input
            id="cin"
            v-model="form.cin"
            placeholder="Numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="cinState"
            aria-describedby="cin-desc"
            autocomplete="off"
            @keypress="allowOnlyNumbers"
          />
          <b-form-text
            id="cin-desc"
            class="sr-only"
          >
            Format : 8 chiffres, sans espaces.
          </b-form-text>
          <b-form-invalid-feedback v-if="form.cin && !cinState">
            Le numéro de CIN doit contenir 8 chiffres
          </b-form-invalid-feedback>
        </b-form-group>

        <!-- Affectation au dépôt -->
        <h5
          id="section-affectation-depot"
          class="mb-2 mt-4"
        >
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Affectation au dépôt
        </h5>

        <b-form-group
          label="Dépôt logistique"
          label-for="repository"
          :invalid-feedback="''"
          :state="repositoryState"
          aria-required="false"
          description="Sélectionnez le dépôt logistique auquel le membre sera affecté (optionnel)."
        >
          <v-select
            id="repository"
            v-model="form.repository"
            :options="repositories"
            label="name"
            :reduce="repo => repo.id"
            placeholder="Sélectionnez un dépôt logistique (optionnel)"
            :class="[
              'enhanced-select',
              form.repository ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            :loading="repositoriesLoading"
            :disabled="repositoriesLoading"
            aria-describedby="repository-desc"
            aria-required="false"
            @input="onRepositoryChange"
          >
            <template #no-options>
              <span v-if="repositoriesLoading">Chargement des dépôts logistiques...</span>
              <span v-else>Aucun dépôt disponible</span>
            </template>
            <template #selected-option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #spinner="{ loading }">
              <div
                v-if="loading"
                class="vs__spinner"
                aria-label="Chargement des dépôts logistiques"
              >
                <div class="loading-dots">
                  <div class="dot1" />
                  <div class="dot2" />
                  <div class="dot3" />
                </div>
              </div>
            </template>
          </v-select>
          <b-form-text
            id="repository-desc"
            class="sr-only"
          >
            Champ optionnel. Sélectionnez le dépôt logistique d’affectation si nécessaire.
          </b-form-text>
        </b-form-group>

        <!-- Informations de connexion -->
        <h5
          id="section-infos-connexion"
          class="mb-2 mt-4"
        >
          <feather-icon
            icon="LockIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations de connexion
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Mot de passe *"
              label-for="password"
              aria-required="true"
              description="Mot de passe d’accès à la plateforme (minimum 8 caractères)."
            >
              <b-input-group class="input-group-merge">
                <b-form-input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  :state="passwordState"
                  placeholder="Entrez un mot de passe sécurisé"
                  aria-describedby="password-desc"
                  aria-required="true"
                  autocomplete="new-password"
                />
                <b-input-group-append
                  is-text
                  aria-label="Afficher ou masquer le mot de passe"
                  @click="showPassword = !showPassword"
                >
                  <feather-icon
                    :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                    class="cursor-pointer"
                    aria-hidden="true"
                  />
                </b-input-group-append>
              </b-input-group>
              <b-form-text
                id="password-desc"
                class="sr-only"
              >
                Champ obligatoire. Le mot de passe doit comporter au moins 8 caractères.
              </b-form-text>
              <b-form-invalid-feedback v-if="submitted && !form.password">
                Le mot de passe est requis
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.password && !passwordState">
                Le mot de passe doit comporter au moins 8 caractères
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Confirmer le mot de passe *"
              label-for="confirm-password"
              aria-required="true"
              description="Saisissez à nouveau le mot de passe pour confirmation."
            >
              <b-input-group class="input-group-merge">
                <b-form-input
                  id="confirm-password"
                  v-model="form.confirm_password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :state="confirmPasswordState"
                  placeholder="Confirmez le mot de passe"
                  aria-describedby="confirm-password-desc"
                  aria-required="true"
                  autocomplete="new-password"
                />
                <b-input-group-append
                  is-text
                  aria-label="Afficher ou masquer la confirmation du mot de passe"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <feather-icon
                    :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                    class="cursor-pointer"
                    aria-hidden="true"
                  />
                </b-input-group-append>
              </b-input-group>
              <b-form-text
                id="confirm-password-desc"
                class="sr-only"
              >
                Champ obligatoire. Saisissez à nouveau le mot de passe pour confirmation.
              </b-form-text>
              <b-form-invalid-feedback v-if="submitted && !form.confirm_password">
                La confirmation du mot de passe est requise
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.confirm_password && !confirmPasswordState">
                Les mots de passe ne correspondent pas
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Boutons d’action -->
        <div
          class="d-flex justify-content-between mt-4"
          role="group"
          aria-label="Actions du formulaire"
        >
          <b-button
            variant="outline-secondary"
            :to="{ name: 'staff-list' }"
            aria-label="Annuler et revenir à la liste du personnel"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="isSubmitting"
            aria-label="Enregistrer le membre du personnel"
          >
            <b-spinner
              v-if="isSubmitting"
              small
              class="mr-1"
              aria-label="Enregistrement en cours"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Enregistrer
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BFormText,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'StaffCreate',

  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BFormText,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      submitted: false,
      repositories: [],
      repositoriesLoading: true,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        password: '',
        confirm_password: '',
        repository: null,
        role: null, // Add role for staff
      },
      showPassword: false,
      showConfirmPassword: false,
      staffRoles: [
        { label: 'Commercial', value: 'sales' },
        { label: 'Magasinier', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptable', value: 'accountant' },
      ],
    }
  },

  computed: {
    nameState() {
      return this.form.username ? true : null
    },

    emailState() {
      if (!this.form.email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(this.form.email)
    },

    passwordState() {
      if (!this.form.password) return null
      return this.form.password.length >= 8
    },

    confirmPasswordState() {
      if (!this.form.confirm_password) return null
      return this.form.confirm_password === this.form.password
    },

    phoneState() {
      if (!this.form.phone) return true
      return this.form.phone.length === 8 && /^\d+$/.test(this.form.phone)
    },

    repositoryState() {
      // Always valid, since repository is not required
      return true
    },

    cinState() {
      if (!this.form.cin) return true // Optional field
      return this.form.cin.length === 8 && /^\d+$/.test(this.form.cin)
    },

    roleState() {
      return this.form.role ? true : null
    },
  },

  created() {
    this.fetchRepositories()
  },

  mounted() {
    // Ensure proper CSS loading after component mount
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  // Handle route changes for proper CSS reloading
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$nextTick(() => {
        vm.refreshSelectStyles()
      })
    })
  },

  activated() {
    // For keep-alive components
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  methods: {
    refreshSelectStyles() {
      // Force refresh of v-select styles
      const selectElements = this.$el.querySelectorAll('.enhanced-select')
      selectElements.forEach(element => {
        // Trigger a reflow to ensure styles are applied
        const originalDisplay = element.style.display
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'none'
        // eslint-disable-next-line no-unused-expressions
        element.offsetHeight // trigger reflow
        // eslint-disable-next-line no-param-reassign
        element.style.display = originalDisplay || ''
      })
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }

      // Also prevent input if already at 8 digits
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data

        // Ensure styles are applied after data loads
        this.$nextTick(() => {
          this.refreshSelectStyles()
        })
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
        console.error('Error fetching repositories:', error)
      } finally {
        this.repositoriesLoading = false
      }
    },

    onRepositoryChange(value) {
      // Handle repository selection change
      this.form.repository = value
      // Clear submitted state for this field if it's now valid
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    validateForm() {
      this.submitted = true

      // Basic validation
      if (!this.form.username) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le nom complet est requis',
        })
        return false
      }

      if (!this.form.email || !this.emailState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Une adresse email valide est requise',
        })
        return false
      }

      if (!this.form.password || !this.passwordState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le mot de passe doit comporter au moins 8 caractères',
        })
        return false
      }

      if (this.form.password !== this.form.confirm_password) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Les mots de passe ne correspondent pas',
        })
        return false
      }

      // repository is not required anymore

      if (!this.form.role) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un rôle',
        })
        return false
      }

      if (this.form.phone && !this.phoneState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de téléphone doit contenir 8 chiffres',
        })
        return false
      }

      if (this.form.cin && !this.cinState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de CIN doit contenir 8 chiffres',
        })
        return false
      }

      return true
    },

    async saveStaff() {
      if (this.isSubmitting) return
      if (!this.validateForm()) return
      this.isSubmitting = true
      try {
        const payload = {
          ...this.form,
          repository: this.form.repository,
          role: this.form.role,
        }
        await axios.post('/api/auth/staff-members/create/', payload)
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Membre du personnel créé avec succès',
        })
        setTimeout(() => {
          this.$router.push({ name: 'staff-list' })
        }, 1000)
      } catch (error) {
        console.error('Error creating staff member:', error)

        // Extract meaningful error message from backend response
        let errorMessage = 'Erreur lors de la création du membre du personnel'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          // Check for specific field errors (like email, phone, username)
          if (errorData.email) {
            const rawError = Array.isArray(errorData.email)
              ? errorData.email[0]
              : errorData.email
            // Transform technical message to user-friendly French
            if (rawError.includes('email') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec cet email existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.phone) {
            const rawError = Array.isArray(errorData.phone)
              ? errorData.phone[0]
              : errorData.phone
            if (rawError.includes('phone') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec ce téléphone existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.username) {
            const rawError = Array.isArray(errorData.username)
              ? errorData.username[0]
              : errorData.username
            if (rawError.includes('username') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec ce nom existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.error) {
            errorMessage = errorData.error
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          } else {
            // If there are multiple field errors, show the first one
            const firstErrorKey = Object.keys(errorData)[0]
            if (firstErrorKey && errorData[firstErrorKey]) {
              const firstError = errorData[firstErrorKey]
              const rawError = Array.isArray(firstError)
                ? firstError[0]
                : firstError

              // Transform based on field name
              if (firstErrorKey === 'email' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec cet email existe déjà'
              } else if (firstErrorKey === 'phone' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec ce téléphone existe déjà'
              } else if (firstErrorKey === 'username' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec ce nom existe déjà'
              } else {
                errorMessage = rawError
              }
            }
          }
        }

        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    showToast({ variant, title, text }) {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'danger') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
    // Placeholder for future role change logic
    onRoleChange() {
      // To be implemented
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.form-group {
  margin-bottom: 1.5rem;
}

.form-control:focus,
.custom-select:focus {
  border-color: #E31E24;
  box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1);
}

/* Enhanced select dropdown styling - global version for better reliability */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 6px 12px !important;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem !important;
    background-color: #fff !important;
    transition: all 0.3s ease-in-out !important;
    min-height: 42px !important;

    &:hover {
      border-color: #b8b6c0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
    background-color: #fff !important;
  }

  &.vs--focused .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  .vs__selected {
    margin: 2px 2px !important;
    padding: 4px 8px !important;
    color: #5e5873 !important;
    background-color: rgba(227, 30, 36, 0.1) !important;
    border-radius: 4px !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
  }

  .vs__selected-options {
    padding: 2px !important;
    flex-wrap: wrap !important;
  }

  .vs__actions {
    padding-right: 8px !important;
  }  .vs__clear {
    color: #b9b9c3 !important;

    &:hover {
      color: #E31E24 !important;
    }
  }

  .vs__open-indicator {
    color: #b9b9c3 !important;
    transition: transform 0.3s ease !important;

    &:hover {
      color: #E31E24 !important;
    }
  }

  &.vs--open .vs__open-indicator {
    transform: rotate(180deg) !important;
  }

  .vs__search {
    padding: 4px 0 !important;
    margin: 0 !important;

    &::placeholder {
      color: #b9b9c3 !important;
    }

    &:focus {
      outline: none !important;
    }
  }

  .vs__dropdown-menu {
    padding: 0 !important;
    border: 1px solid #d8d6de !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
    border-radius: 0.357rem !important;
    background: #fff !important;
    z-index: 1050 !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }

  .vs__dropdown-option {
    padding: 12px 20px !important;
    color: #5e5873 !important;
    transition: all 0.2s ease !important;
    cursor: pointer !important;
    border-bottom: 1px solid #f0f0f0 !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover, &--highlight {
      background: linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%) !important;
      color: #E31E24 !important;
    }

    &--selected {
      background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%) !important;
      color: #fff !important;
      font-weight: 600 !important;
    }

    &--disabled {
      background: #f5f5f5 !important;
      color: #b9b9c3 !important;
      cursor: not-allowed !important;
    }
  }

  .vs__no-options {
    padding: 20px !important;
    text-align: center !important;
    color: #b9b9c3 !important;
    font-style: italic !important;
  }

  .vs__spinner {
    border-color: #E31E24 transparent transparent !important;
  }

  /* Focus state enhancement */
  &:focus-within .vs__dropdown-toggle {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  /* Error state styling */
  &.is-invalid .vs__dropdown-toggle {
    border-color: #ea5455 !important;

    &:focus, &:hover {
      border-color: #ea5455 !important;
      box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.1) !important;
    }
  }

  /* Success state styling */
  &.is-valid .vs__dropdown-toggle {
    border-color: #28c76f !important;

    &:focus, &:hover {
      border-color: #28c76f !important;
      box-shadow: 0 3px 10px 0 rgba(40, 199, 111, 0.1) !important;
    }
  }
}

/* Ensure proper z-index for dropdowns */
.vs__dropdown-menu {
  z-index: 1050 !important;
}

/* Form enhancements */
.card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
  border: none !important;
  border-radius: 0.75rem !important;
}

.card-title {
  font-weight: 700 !important;
  color: #5e5873 !important;
}

h5 {
  color: #5e5873 !important;
  font-weight: 600 !important;
  margin-bottom: 1rem !important;

  .feather {
    color: #E31E24 !important;
  }
}

/* Form labels styling */
.form-group label {
  font-weight: 600 !important;
  color: #5e5873 !important;
  margin-bottom: 0.5rem !important;
}

/* Form controls enhanced styling */
.form-control {
  border: 1px solid #d8d6de !important;
  border-radius: 0.357rem !important;
  padding: 0.75rem 1rem !important;
  transition: all 0.3s ease !important;

  &:focus {
    border-color: #E31E24 !important;
    box-shadow: 0 3px 10px 0 rgba(227, 30, 36, 0.1) !important;
  }

  &:hover {
    border-color: #b8b6c0 !important;
  }
}

/* Button styling */
.btn-primary {
  background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(227, 30, 36, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(227, 30, 36, 0.4) !important;
  }
}

.btn-outline-secondary {
  border-color: #d8d6de !important;
  color: #5e5873 !important;

  &:hover {
    background-color: #f8f8f8 !important;
    border-color: #b8b6c0 !important;
    color: #5e5873 !important;
  }
}

/* Force CSS re-application after navigation */
.page-enter-active, .page-leave-active {
  transition: opacity 0.3s;
}

.page-enter, .page-leave-to {
  opacity: 0;
}

/* Ensure v-select is always properly styled */
.enhanced-select * {
  box-sizing: border-box !important;
}

/* Fix for navigation CSS loading issues */
.router-view {
  transition: all 0.3s ease;
}

/* Loading state for repository select */
.enhanced-select.vs--loading .vs__open-indicator {
  animation: spin 1s linear infinite !important;
}

.enhanced-select .vs__spinner {
  .loading-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    div {
      width: 4px;
      height: 4px;
      margin: 0 2px;
      background-color: #E31E24;
      border-radius: 50%;
      animation: loading-bounce 1.4s ease-in-out infinite both;
    }

    .dot1 { animation-delay: -0.32s; }
    .dot2 { animation-delay: -0.16s; }
    .dot3 { animation-delay: 0s; }
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhancement for better visual feedback */
.form-group.has-error .enhanced-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
  animation: shake 0.5s ease-in-out !important;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Ensure proper z-index stacking */
.enhanced-select .vs__dropdown-menu {
  z-index: 9999 !important;
  position: absolute !important;
}

/* Fix for modal/overlay issues */
.modal .enhanced-select .vs__dropdown-menu {
  z-index: 1060 !important;
}
</style>
